<template>
  <main class="min-h-screen bg-slate-50/70 dark:bg-[#070c18] relative flex items-center justify-center p-4 py-8 sm:py-12 overflow-hidden">
    <!-- Ambient Glow Accents (Pure Blur, No Box Shadows) -->
    <div class="absolute -top-40 -right-40 w-96 h-96 bg-purple-500/15 dark:bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/15 dark:bg-indigo-600/10 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/5 dark:bg-violet-500/5 rounded-full blur-3xl pointer-events-none"></div>

    <!-- Theme Toggle -->
    <div class="absolute top-4 right-4 z-50">
      <ThemeToggle />
    </div>

    <!-- Master Container -->
    <div class="w-full max-w-5xl relative z-10 space-y-6">

      <!-- Setup Steps Wizard Bar (Exact Image Reproduction - Flat & Shadow-Free) -->
      <div
        class="bg-white/95 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl border border-slate-200/80 dark:border-slate-800/80 px-6 py-4 sm:py-5"
      >
        <div
          class="flex items-center justify-center gap-4 sm:gap-8 md:gap-12 overflow-x-auto select-none py-1"
        >
          <!-- Step 1: Sign Up -->
          <div class="flex items-center gap-3.5 shrink-0">
            <div
              class="w-12 h-12 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold text-base select-none"
            >
              1
            </div>
            <div class="text-left">
              <h4 class="font-bold text-sm sm:text-base text-slate-900 dark:text-white leading-tight">
                Sign Up
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Create your account
              </p>
            </div>
          </div>

          <!-- Horizontal Divider -->
          <span class="text-2xl font-light text-slate-300 dark:text-slate-600 shrink-0 select-none px-1" aria-hidden="true">
            —
          </span>

          <!-- Step 2: Create School -->
          <div class="flex items-center gap-3.5 shrink-0">
            <div
              class="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-300 font-bold text-base select-none"
            >
              2
            </div>
            <div class="text-left">
              <h4 class="font-bold text-sm sm:text-base text-slate-800 dark:text-slate-200 leading-tight">
                Create School
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Set up your school profile
              </p>
            </div>
          </div>

          <!-- Horizontal Divider -->
          <span class="text-2xl font-light text-slate-300 dark:text-slate-600 shrink-0 select-none px-1" aria-hidden="true">
            —
          </span>

          <!-- Step 3: Add Classes -->
          <div class="flex items-center gap-3.5 shrink-0">
            <div
              class="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-300 font-bold text-base select-none"
            >
              3
            </div>
            <div class="text-left">
              <h4 class="font-bold text-sm sm:text-base text-slate-800 dark:text-slate-200 leading-tight">
                Add Classes
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Create your first class
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Two-Panel Card (No Shadows) -->
      <div
        class="bg-white/95 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl border border-slate-200/80 dark:border-slate-800/80 overflow-hidden grid grid-cols-1 lg:grid-cols-12 min-h-[580px]"
      >
        <!-- LEFT PANEL: Brand Showcase & Value Props (5 Cols) -->
        <div
          class="lg:col-span-5 bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-900 p-8 sm:p-10 text-white flex flex-col justify-between relative overflow-hidden"
        >
          <!-- Subtle Decorative Graphic Rings -->
          <div class="absolute -top-12 -right-12 w-56 h-56 rounded-full border border-white/10 pointer-events-none"></div>
          <div class="absolute -top-4 -right-4 w-40 h-40 rounded-full border border-white/15 pointer-events-none"></div>
          <div class="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-violet-500/20 blur-2xl pointer-events-none"></div>

          <!-- Top Brand Header -->
          <div class="relative z-10">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20">
                <GraduationCap class="w-6 h-6 text-white" />
              </div>
              <div>
                <span class="text-xl font-bold tracking-tight text-white">Educanium</span>
                <span class="block text-[11px] font-medium text-indigo-200">Smart School Suite</span>
              </div>
            </div>

            <!-- Hero Headline -->
            <h2 class="text-2xl sm:text-3xl font-bold leading-tight tracking-tight text-white mt-4">
              Empower your institution with next-gen smart tools.
            </h2>
            <p class="text-xs sm:text-sm text-indigo-100/80 mt-2.5 leading-relaxed">
              Automate biometric attendance, communicate with parents via WhatsApp, and manage academics effortlessly.
            </p>
          </div>

          <!-- Feature Cards / Value Props -->
          <div class="space-y-3.5 my-8 relative z-10">
            <div class="flex items-start gap-3 p-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15">
              <div class="w-8 h-8 rounded-xl bg-indigo-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Sparkles class="w-4 h-4 text-amber-300" />
              </div>
              <div>
                <h4 class="text-xs font-semibold text-white">3-Minute Setup</h4>
                <p class="text-[11px] text-indigo-100/70">Create admin account, profile your school, and add classes instantly.</p>
              </div>
            </div>

            <div class="flex items-start gap-3 p-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15">
              <div class="w-8 h-8 rounded-xl bg-indigo-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Smartphone class="w-4 h-4 text-emerald-300" />
              </div>
              <div>
                <h4 class="text-xs font-semibold text-white">WhatsApp & SMS Live Sync</h4>
                <p class="text-[11px] text-indigo-100/70">Instant alerts for attendance, fees, exams, and emergency circulars.</p>
              </div>
            </div>

            <div class="flex items-start gap-3 p-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15">
              <div class="w-8 h-8 rounded-xl bg-indigo-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <ShieldCheck class="w-4 h-4 text-cyan-300" />
              </div>
              <div>
                <h4 class="text-xs font-semibold text-white">Enterprise Privacy</h4>
                <p class="text-[11px] text-indigo-100/70">Encrypted student records, strict role-based access & auto-backups.</p>
              </div>
            </div>
          </div>

          <!-- Bottom Trust Indicator -->
          <div class="pt-4 border-t border-white/15 flex items-center justify-between text-[11px] text-indigo-200 relative z-10">
            <span class="flex items-center gap-1.5 font-medium">
              <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              99.9% Platform Availability
            </span>
            <span class="text-white/60">v2.4 Enterprise</span>
          </div>
        </div>

        <!-- RIGHT PANEL: Action Form (7 Cols) -->
        <div class="lg:col-span-7 p-6 sm:p-10 flex flex-col justify-between">
          <div>
            <!-- FORM PHASE 1: Account Information -->
            <div v-if="phase === 'form'" class="animate-fade-in">
              <div class="mb-6 text-left">
                <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50 mb-2.5">
                  <User class="w-3.5 h-3.5 text-indigo-500" />
                  <span>Step 1: Administrator Account</span>
                </div>
                <h3 class="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Get started with Educanium
                </h3>
                <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Create your administrator credentials to set up and manage your school.
                </p>
              </div>

              <form @submit.prevent="onSubmitAccount" class="space-y-4" novalidate>
                <!-- Full Name -->
                <div class="space-y-1.5 text-left">
                  <label
                    for="signup-name"
                    class="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider"
                  >
                    Full Name <span class="text-rose-500">*</span>
                  </label>
                  <div
                    class="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/70 dark:bg-slate-800/50 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 dark:focus-within:border-indigo-400 transition-all duration-200"
                  >
                    <span class="pl-3.5 pr-2 text-slate-400 dark:text-slate-500 flex items-center pointer-events-none">
                      <User class="w-4 h-4 text-indigo-500" />
                    </span>
                    <input
                      ref="firstFieldRef"
                      id="signup-name"
                      v-model.trim="form.name"
                      type="text"
                      required
                      autocomplete="name"
                      placeholder="e.g. Dr. Alex Morgan"
                      class="w-full py-3 pr-3.5 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
                    />
                  </div>
                </div>

                <!-- Phone Number -->
                <div class="space-y-1.5 text-left">
                  <div class="flex items-center justify-between">
                    <label
                      for="signup-phone"
                      class="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider"
                    >
                      Phone Number <span class="text-rose-500">*</span>
                    </label>
                    <span class="text-[11px] text-slate-400 dark:text-slate-500">10-Digit Mobile</span>
                  </div>
                  <div
                    class="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/70 dark:bg-slate-800/50 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 dark:focus-within:border-indigo-400 transition-all duration-200"
                  >
                    <span class="pl-3.5 pr-2 text-slate-400 dark:text-slate-500 flex items-center pointer-events-none">
                      <Phone class="w-4 h-4 text-indigo-500" />
                    </span>
                    <span class="text-xs font-bold text-slate-500 dark:text-slate-400 pr-2 border-r border-slate-200 dark:border-slate-700">
                      +91
                    </span>
                    <input
                      id="signup-phone"
                      v-model.trim="form.phone"
                      type="tel"
                      inputmode="tel"
                      required
                      autocomplete="tel"
                      placeholder="9876543210"
                      class="w-full py-3 px-3 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
                    />
                  </div>
                </div>

                <!-- Email Address (Optional) -->
                <div class="space-y-1.5 text-left">
                  <div class="flex items-center justify-between">
                    <label
                      for="signup-email"
                      class="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider"
                    >
                      Email Address
                    </label>
                    <span class="text-[11px] text-slate-400 dark:text-slate-500 font-medium">Recommended for alerts</span>
                  </div>
                  <div
                    class="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/70 dark:bg-slate-800/50 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 dark:focus-within:border-indigo-400 transition-all duration-200"
                  >
                    <span class="pl-3.5 pr-2 text-slate-400 dark:text-slate-500 flex items-center pointer-events-none">
                      <Mail class="w-4 h-4 text-indigo-500" />
                    </span>
                    <input
                      id="signup-email"
                      v-model.trim="form.email"
                      type="email"
                      autocomplete="email"
                      placeholder="principal@school.com"
                      class="w-full py-3 pr-3.5 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
                    />
                  </div>
                </div>

                <!-- Alerts -->
                <transition name="fade">
                  <div
                    v-if="error"
                    class="flex items-start gap-2.5 p-3 rounded-xl text-xs sm:text-sm bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-left animate-shake"
                  >
                    <AlertCircle class="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-500" />
                    <span class="flex-1 leading-snug">{{ error }}</span>
                  </div>
                </transition>

                <transition name="fade">
                  <div
                    v-if="success"
                    class="flex items-start gap-2.5 p-3 rounded-xl text-xs sm:text-sm bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-left"
                  >
                    <CheckCircle2 class="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                    <span class="flex-1 leading-snug">{{ success }}</span>
                  </div>
                </transition>

                <!-- Submit Button (Flat, No Box Shadows) -->
                <button
                  type="submit"
                  :disabled="loading"
                  class="w-full mt-4 bg-gradient-to-r from-indigo-600 via-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold py-3.5 px-4 rounded-xl border border-transparent active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
                  <Send v-else class="w-4 h-4" />
                  <span>{{ loading ? "Dispatching Verification..." : "Continue to Verification &rarr;" }}</span>
                </button>
              </form>
            </div>

            <!-- FORM PHASE 2: OTP Verification -->
            <div v-else class="animate-fade-in text-left">
              <div class="mb-6">
                <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50 mb-2">
                  <ShieldCheck class="w-3.5 h-3.5 text-indigo-500" />
                  <span>Security Verification</span>
                </div>
                <h3 class="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Confirm your credentials
                </h3>
                <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Enter the 6-digit one-time password dispatched to your mobile / email.
                </p>
              </div>

              <!-- Target Pill -->
              <div
                class="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 mb-5"
              >
                <div class="flex items-center gap-3 min-w-0">
                  <div class="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 flex items-center justify-center flex-shrink-0">
                    <KeyRound class="w-4 h-4" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Verification code target
                    </p>
                    <p class="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                      {{ otpSentVia === "email" ? form.email : maskedPhone }}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  @click="
                    phase = 'form';
                    resetMessages();
                    otp = '';
                    stopCountdown();
                    resendDisabled = false;
                  "
                  class="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 px-3 py-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <Edit3 class="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>
              </div>

              <form @submit.prevent="onVerifyOtp" class="space-y-4" novalidate>
                <!-- OTP Digits Input -->
                <div class="space-y-1.5">
                  <label
                    for="signup-otp"
                    class="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider"
                  >
                    6-Digit One-Time Code
                  </label>
                  <div
                    class="relative flex items-center rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/70 dark:bg-slate-800/50 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 dark:focus-within:border-indigo-400 transition-all duration-200"
                  >
                    <input
                      ref="otpInputRef"
                      id="signup-otp"
                      v-model.trim="otp"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      maxlength="6"
                      autocomplete="one-time-code"
                      placeholder="000000"
                      class="w-full py-4 px-4 bg-transparent text-center tracking-[0.45em] font-mono text-2xl font-bold text-slate-900 dark:text-slate-100 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none"
                    />
                  </div>
                </div>

                <!-- Resend Row -->
                <div class="flex items-center justify-between text-xs pt-1">
                  <span class="text-slate-500 dark:text-slate-400">Didn't receive the SMS / WhatsApp?</span>
                  <button
                    type="button"
                    class="font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    :disabled="resendDisabled || loading"
                    @click="onResendOtp"
                  >
                    <span>Resend Code</span>
                    <span v-if="resendDisabled" class="ml-1 font-mono">({{ countdown }}s)</span>
                  </button>
                </div>

                <!-- Alerts -->
                <transition name="fade">
                  <div
                    v-if="error"
                    class="flex items-start gap-2.5 p-3 rounded-xl text-xs sm:text-sm bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-left animate-shake"
                  >
                    <AlertCircle class="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-500" />
                    <span class="flex-1 leading-snug">{{ error }}</span>
                  </div>
                </transition>

                <transition name="fade">
                  <div
                    v-if="success"
                    class="flex items-start gap-2.5 p-3 rounded-xl text-xs sm:text-sm bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-left"
                  >
                    <CheckCircle2 class="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                    <span class="flex-1 leading-snug">{{ success }}</span>
                  </div>
                </transition>

                <!-- Action Button (Flat, No Box Shadows) -->
                <button
                  type="submit"
                  :disabled="loading || otp.length !== 6"
                  class="w-full mt-4 bg-gradient-to-r from-indigo-600 via-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold py-3.5 px-4 rounded-xl border border-transparent active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
                  <CheckCircle2 v-else class="w-4 h-4" />
                  <span>{{ loading ? "Completing Setup..." : "Verify & Start Onboarding" }}</span>
                </button>
              </form>
            </div>
          </div>

          <!-- Bottom Footer Navigation -->
          <div class="pt-6 border-t border-slate-100 dark:border-slate-800 text-center">
            <p class="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
              Already have an account?
              <RouterLink
                to="/login"
                class="font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors ml-1"
              >
                Sign In to Dashboard &rarr;
              </RouterLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, onBeforeUnmount, computed, watch, nextTick } from "vue";
import axios from "axios";
import {
  User,
  Phone,
  Send,
  Loader2,
  ShieldCheck,
  KeyRound,
  CheckCircle2,
  Edit3,
  Mail,
  GraduationCap,
  Sparkles,
  Smartphone,
  AlertCircle,
} from "lucide-vue-next";

import { useRouter } from "vue-router";
import { useStore } from "vuex";
import jwt_decode from "jwt-decode";
import ThemeToggle from "@/components/Header/ThemeToggle/ThemeToggle.vue";

const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";

const router = useRouter();
const store = useStore();

const sendOtpUrl = `${baseURL}/api/auth/signup/send-otp`;
const signupUrl = `${baseURL}/api/auth/signup`;

const otpSentVia = ref("phone");
const error = ref("");
const success = ref("");
const loading = ref(false);
const otpInputRef = ref(null);

const phase = ref("form"); // 'form' | 'otp'
const otp = ref("");
const resendDisabled = ref(false);
const countdown = ref(60);
let countdownTimer = null;

const form = ref({
  name: "",
  phone: "",
  email: "",
});

const firstFieldRef = ref(null);
const maskedPhone = computed(() => maskPhone(form.value.phone));

onBeforeUnmount(() => {
  stopCountdown();
});

function resetMessages() {
  error.value = "";
  success.value = "";
}

function normalizePhone(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
}

function maskPhone(phone) {
  const p = normalizePhone(phone);
  if (p.length <= 4) return "****";
  return p.slice(0, 2) + "******" + p.slice(-2);
}

function validateAccount() {
  const phone = normalizePhone(form.value.phone);

  if (!form.value.name || !form.value.phone) {
    error.value = "Please fill out all required fields.";
    return false;
  }
  if (form.value.email && !/^\S+@\S+\.\S+$/.test(form.value.email)) {
    error.value = "Please enter a valid email address.";
    return false;
  }

  if (!/^\d{10}$/.test(phone)) {
    error.value = "Please enter a valid 10-digit mobile number.";
    return false;
  }

  return true;
}

// OTP countdown
function startCountdown() {
  resendDisabled.value = true;
  countdown.value = 60;
  stopCountdown();
  countdownTimer = setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) {
      stopCountdown();
      resendDisabled.value = false;
    }
  }, 1000);
}

function stopCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

// API: Submit Account Form
async function onSubmitAccount() {
  resetMessages();
  if (!validateAccount()) return;

  loading.value = true;
  try {
    const payload = {
      name: form.value.name,
      phone: normalizePhone(form.value.phone),
      email: form.value.email || undefined,
    };
    const res = await axios.post(sendOtpUrl, payload);
    success.value = res.data?.message || "OTP sent successfully.";
    if (res.data?.devOtp) {
      otp.value = res.data.devOtp;
    }

    if (res.data?.message?.toLowerCase().includes("email") && !res.data?.message?.toLowerCase().includes("phone")) {
      otpSentVia.value = "email";
    } else {
      otpSentVia.value = "phone";
    }

    phase.value = "otp";
    startCountdown();
    await nextTick();
    otpInputRef.value?.focus();
  } catch (err) {
    console.error("OTP Error:", err.response?.data);
    error.value =
      err.response?.data?.error ||
      err.response?.data?.message ||
      "🚨 Server error while sending OTP. Please try again later.";
  } finally {
    loading.value = false;
  }
}

// API: Resend OTP
async function onResendOtp() {
  resetMessages();
  loading.value = true;
  try {
    const payload = {
      name: form.value.name,
      phone: normalizePhone(form.value.phone),
      email: form.value.email || undefined,
    };
    const res = await axios.post(sendOtpUrl, payload);
    success.value = res.data?.message || "OTP re-sent successfully.";

    if (res.data?.message?.toLowerCase().includes("email") && !res.data?.message?.toLowerCase().includes("phone")) {
      otpSentVia.value = "email";
    } else {
      otpSentVia.value = "phone";
    }

    startCountdown();
    await nextTick();
    otpInputRef.value?.focus();
  } catch (err) {
    console.error("Resend OTP Error:", err.response?.data);
    error.value =
      err.response?.data?.error ||
      err.response?.data?.message ||
      "🚨 Server error while resending OTP. Please try again later.";
  } finally {
    loading.value = false;
  }
}

// API: Verify OTP & Complete Signup
async function onVerifyOtp() {
  resetMessages();
  if (!otp.value || otp.value.length !== 6) {
    error.value = "Please enter the 6-digit OTP.";
    return;
  }

  loading.value = true;
  try {
    const payload = {
      phone: normalizePhone(form.value.phone),
      otp: otp.value,
      name: form.value.name || undefined,
      email: form.value.email || undefined,
    };
    const res = await axios.post(signupUrl, payload);
    const data = res.data;

    if (data.token) {
      const decoded = jwt_decode(data.token);
      localStorage.setItem("token", data.token);

      if (decoded.schoolId) store.commit("SET_SCHOOL_ID", decoded.schoolId);
      if (decoded.role) store.commit("SET_USER_ROLE", decoded.role);
      if (decoded.userId) store.commit("SET_USER_ID", decoded.userId);
      if (decoded.name) store.commit("SET_USER_NAME", decoded.name);

      await store.dispatch("initializeApp");
    }

    success.value = data?.message || "Account created successfully.";
    router.replace("/onboarding");
  } catch (err) {
    console.error("Verify OTP Error:", err);
    if (err.response?.data) {
      error.value =
        err.response.data.message ||
        err.response.data.error ||
        JSON.stringify(err.response.data);
    } else if (err.message) {
      error.value = err.message;
    } else {
      error.value = "🚨 Verification failed. Please check the code and try again.";
    }
  } finally {
    loading.value = false;
  }
}

watch(otp, (value) => {
  if (value.length === 6 && phase.value === "otp" && !loading.value) {
    onVerifyOtp();
  }
});
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}
.animate-shake {
  animation: shake 0.3s ease-in-out;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
